// frontend/app/components/lessonPlans/authoring/LessonWizard.js
//
// The lesson authoring wizard. Modelled on components/tests/CreateEditTestWizard
// rather than inventing a second wizard pattern for this codebase.
//
// The shell owns the whole draft; the four steps are presentational. Saving
// goes through one payload builder (lessonDraft.toApiPayload) so what the
// Review step validates is exactly what gets sent.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from "react-native";

import { WizardFooter, WizardHeader, WizardStepRail } from "./WizardChrome";
import { LessonAuthoringContext } from "./LessonAuthoringContext";
import WizardStepConcepts from "./WizardStepConcepts";
import WizardStepDetails from "./WizardStepDetails";
import WizardStepPhases from "./WizardStepPhases";
import WizardStepReview from "./WizardStepReview";
import { getLessonColors } from "../lessonPlanTheme";
import {
  conceptsFromLesson,
  defaultLoopSteps,
  draftFromLesson,
  emptyDraft,
  slugify,
  stepsFromLesson,
  toApiPayload,
  validateDraft,
} from "./lessonDraft";
import {
  apiCreateLessonPlan,
  apiGetLessonPlanRuntime,
  apiGetTopicAssetCatalog,
  apiPatchLessonPlan,
} from "../../../lessonPlans";

const LessonWizard = ({
  isDarkMode = false,
  board,
  curriculum = [],
  ownerEmail,
  fetchWithAuth,
  editLessonPlanId = null,
  onClose,
  onSaved,
  t,
}) => {
  const translate = t || ((key) => key);
  const colors = getLessonColors(isDarkMode);

  const STEPS = [
    { num: 1, title: translate("Details"), icon: "text-box-outline" },
    { num: 2, title: translate("Concepts"), icon: "tag-multiple-outline" },
    { num: 3, title: translate("Lesson flow"), icon: "format-list-numbered" },
    { num: 4, title: translate("Review"), icon: "check-decagram-outline" },
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [draft, setDraft] = useState(() => emptyDraft(board));
  const [concepts, setConcepts] = useState([]);
  // A new lesson opens as the whole Loop rather than as a blank flow — the
  // seven steps are the pedagogy, not a template someone has to remember.
  const [steps, setSteps] = useState(() =>
    editLessonPlanId ? [] : defaultLoopSteps(translate)
  );

  const [loading, setLoading] = useState(Boolean(editLessonPlanId));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lessonPlanId, setLessonPlanId] = useState(editLessonPlanId);

  const [unresolved, setUnresolved] = useState(null);
  const [checkingAssets, setCheckingAssets] = useState(false);
  // The authoring gate from `services/lesson_shape.py`, read off the same
  // runtime payload as `unresolved` — null until checked, same tri-state.
  const [shapeFindings, setShapeFindings] = useState(null);

  // The topic's real scenes and video tags, for the asset picker. Fetched once
  // per topic here rather than per block, which would be a request per picker.
  const [catalog, setCatalog] = useState({ pwos: [], videos: [], loading: false, error: null });

  const isEditing = Boolean(lessonPlanId);

  // ---- load an existing lesson ------------------------------------------
  useEffect(() => {
    if (!editLessonPlanId) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const lesson = await apiGetLessonPlanRuntime(fetchWithAuth, editLessonPlanId, {
          mode: "teacher",
        });
        if (cancelled) return;
        setDraft(draftFromLesson(lesson));
        setConcepts(conceptsFromLesson(lesson));
        setSteps(stepsFromLesson(lesson));
        setShapeFindings(lesson?.validation?.shape || []);
      } catch (error) {
        if (!cancelled) setSaveError(error?.message || translate("Could not load this lesson."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editLessonPlanId, fetchWithAuth]);

  useEffect(() => {
    const { board_topic_id: topicId, source_topic_id: sourceId, base_board } = draft;
    if (!topicId || !base_board) {
      setCatalog({ pwos: [], videos: [], loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    setCatalog((current) => ({ ...current, loading: true, error: null }));

    (async () => {
      try {
        const next = await apiGetTopicAssetCatalog(fetchWithAuth, {
          board: base_board,
          boardTopicId: topicId,
          sourceTopicId: sourceId ?? topicId,
        });
        if (!cancelled) setCatalog({ ...next, loading: false, error: null });
      } catch (error) {
        if (cancelled) return;
        // Not fatal: the manual-entry fallback in BlockEditor still works.
        setCatalog({
          pwos: [],
          videos: [],
          loading: false,
          error: error?.message || translate("Could not load this topic's content"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draft.board_topic_id, draft.source_topic_id, draft.base_board, fetchWithAuth]);

  // Suggest a web address from the title until the author edits it or the
  // lesson exists — changing a live slug would break any link to it.
  const handleDraftChange = useCallback(
    (next) => {
      const titleChanged = next.title !== draft.title;
      const slugUntouched = draft.slug === slugify(draft.title);
      if (!isEditing && titleChanged && slugUntouched) {
        next = { ...next, slug: slugify(next.title) };
      }
      setDraft(next);
    },
    [draft, isEditing]
  );

  const validation = useMemo(
    () => validateDraft({ draft, concepts, steps, t }),
    [draft, concepts, steps, t]
  );

  // ---- save --------------------------------------------------------------
  const save = useCallback(
    async ({ closeAfter = false } = {}) => {
      setSaving(true);
      setSaveError(null);
      try {
        const payload = toApiPayload({ draft, concepts, steps, ownerEmail });

        let saved;
        if (isEditing) {
          // slug and base_board are omitted deliberately: PATCH does not accept
          // them, and a lesson's address and board are fixed once it exists.
          // schema_version stays in: the steps in this draft are in Loop
          // vocabulary whatever the row says, so re-saving must record that.
          const { slug, base_board, owner_email, ...patch } = payload;
          saved = await apiPatchLessonPlan(fetchWithAuth, lessonPlanId, patch);
        } else {
          saved = await apiCreateLessonPlan(fetchWithAuth, payload);
          setLessonPlanId(saved?.lesson_plan_id ?? saved?.id ?? null);
        }

        if (onSaved) onSaved(saved);
        if (closeAfter && onClose) onClose(saved);
        return saved;
      } catch (error) {
        setSaveError(error?.message || translate("Could not save this lesson."));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [
      concepts,
      draft,
      fetchWithAuth,
      isEditing,
      lessonPlanId,
      onClose,
      onSaved,
      ownerEmail,
      steps,
      translate,
    ]
  );

  /**
   * Asset resolution is server-side only. Save first so the server is checking
   * what the author is actually looking at, then read back the runtime's
   * unresolved list.
   */
  const checkAssets = useCallback(async () => {
    setCheckingAssets(true);
    try {
      const saved = await save();
      const id = saved?.lesson_plan_id ?? saved?.id ?? lessonPlanId;
      if (!id) return;

      const runtime = await apiGetLessonPlanRuntime(fetchWithAuth, id, {
        mode: "teacher",
      });
      setUnresolved(runtime?.validation?.unresolved_blocks || []);
      setShapeFindings(runtime?.validation?.shape || []);
    } catch (error) {
      setSaveError(error?.message || translate("Could not check content."));
    } finally {
      setCheckingAssets(false);
    }
  }, [fetchWithAuth, lessonPlanId, save, translate]);

  // Rows the picker fetched from other topics, so a cross-topic pick keeps its
  // real name in the field. Session-only; deduped by the ref's identity.
  const [browsedRows, setBrowsedRows] = useState({ pwos: [], videos: [] });
  const rememberRows = useCallback((fetched) => {
    const merge = (existing, incoming, keyOf) => {
      const seen = new Set(existing.map(keyOf));
      const fresh = (incoming || []).filter((row) => {
        const key = keyOf(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return fresh.length ? [...existing, ...fresh] : existing;
    };
    setBrowsedRows((current) => {
      const pwos = merge(current.pwos, fetched?.pwos, (r) => `${r.source_pwo_id}:${r.scene_name}`);
      const videos = merge(
        current.videos,
        fetched?.videos,
        (r) => `${r.source_video_id}:${r.tag_id ?? ""}`
      );
      return pwos === current.pwos && videos === current.videos
        ? current
        : { pwos, videos };
    });
  }, []);

  // What a nested asset picker needs to name this lesson's topic and browse others.
  const authoringContext = useMemo(
    () => ({
      board: draft.base_board,
      curriculum,
      fetchWithAuth,
      lessonTopic: {
        board_topic_id: draft.board_topic_id,
        source_topic_id: draft.source_topic_id,
        topic_name: draft.topic_name,
        level_id: draft.level_id,
        subject_id: draft.subject_id,
        chapter_id: draft.chapter_id,
      },
      browsedRows,
      rememberRows,
    }),
    [
      browsedRows,
      curriculum,
      draft.base_board,
      draft.board_topic_id,
      draft.chapter_id,
      draft.level_id,
      draft.source_topic_id,
      draft.subject_id,
      draft.topic_name,
      fetchWithAuth,
      rememberRows,
    ]
  );

  if (loading) {
    return (
      <View style={[styles.loading, { backgroundColor: colors.pageBg }]}>
        <ActivityIndicator size="large" color={colors.accentPrimary} />
        <Text style={[styles.loadingText, { color: colors.textMuted }]}>
          {translate("Loading lesson…")}
        </Text>
      </View>
    );
  }

  // Asset resolution is the one check the author cannot do locally, and the one
  // whose failure a class actually feels. Loop warnings stay advisory.
  const canPublish =
    draft.status !== "published" ||
    (!validation.blocking && (unresolved || []).length === 0);

  return (
    <LessonAuthoringContext.Provider value={authoringContext}>
    <View style={[styles.container, { backgroundColor: colors.pageBg }]}>
      <WizardHeader
        colors={colors}
        eyebrow={isEditing ? translate("Editing lesson") : translate("New lesson")}
        title={draft.title || translate("Untitled lesson")}
        saving={saving}
        saveLabel={translate("Save")}
        closeLabel={translate("Close editor")}
        onClose={() => onClose && onClose(null)}
        onSave={() => save()}
      />

      <WizardStepRail
        colors={colors}
        steps={STEPS}
        currentStep={currentStep}
        onSelect={setCurrentStep}
        stepLabel={translate("Step")}
      />

      {/* Body --------------------------------------------------------- */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        showsVerticalScrollIndicator={false}
      >
        {currentStep === 1 ? (
          <WizardStepDetails
            colors={colors}
            draft={draft}
            onChange={handleDraftChange}
            errors={validation.details}
            curriculum={curriculum}
            isEditing={isEditing}
            t={t}
          />
        ) : null}

        {currentStep === 2 ? (
          <WizardStepConcepts
            colors={colors}
            concepts={concepts}
            objectives={draft.objectives}
            onChange={setConcepts}
            errors={validation.concepts}
            t={t}
          />
        ) : null}

        {currentStep === 3 ? (
          <WizardStepPhases
            colors={colors}
            steps={steps}
            concepts={concepts}
            boardTopicId={draft.board_topic_id}
            catalog={catalog}
            onChange={setSteps}
            errors={validation.phases}
            t={t}
          />
        ) : null}

        {currentStep === 4 ? (
          <WizardStepReview
            colors={colors}
            draft={draft}
            concepts={concepts}
            steps={steps}
            validation={validation}
            unresolved={unresolved}
            shapeFindings={shapeFindings}
            checkingAssets={checkingAssets}
            onCheckAssets={checkAssets}
            onChangeStatus={(status) => setDraft({ ...draft, status })}
            saveError={saveError}
            t={t}
          />
        ) : null}
      </ScrollView>

      <WizardFooter
        colors={colors}
        currentStep={currentStep}
        lastStep={STEPS.length}
        onBack={() => setCurrentStep(currentStep - 1)}
        onNext={() => setCurrentStep(currentStep + 1)}
        onFinish={() => save({ closeAfter: true })}
        backLabel={translate("Back")}
        nextLabel={translate("Next")}
        finishLabel={
          draft.status === "published"
            ? translate("Save & publish")
            : translate("Save draft")
        }
        finishDisabled={saving || !canPublish}
        note={
          validation.blocking
            ? `${
                (validation.blockProblems || []).length +
                Object.keys(validation.details || {}).length +
                Object.keys(validation.phases || {}).length +
                Object.keys(validation.concepts || {}).length
              } ${translate("to fix")}`
            : null
        }
      />
    </View>
    </LessonAuthoringContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, width: "100%", minHeight: 0 },
  loading: { flex: 1, alignItems: "center", justifyContent: "center", gap: 10 },
  loadingText: { fontSize: 13 },

  body: { flex: 1 },
  bodyContent: { padding: 12, paddingBottom: 24 },
});

export default LessonWizard;
